import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AudioWaveform,
  Gauge,
  Guitar,
  SlidersHorizontal,
  Music2,
  Activity,
  Sparkles,
  CheckCircle2,
  LogIn,
  Lock,
} from 'lucide-react';
import AudioUploader from '../components/AudioUploader';
import { TrackSearch } from '../components/TrackSearch';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AILoader } from '../components/ui/ai-loader';
import { MultiStepLoader } from '../components/ui/multi-step-loader';
import { useAuth } from '../contexts/AuthContext';
import { analyzeAudio } from '../lib/api';
import './AnalyzePage.css';

const FEATURES = [
  {
    icon: AudioWaveform,
    title: 'Musical Structure',
    desc: 'Automatic detection of intro, verse, chorus, drop, breakdown, and outro sections.',
  },
  {
    icon: Gauge,
    title: 'BPM & Key',
    desc: 'Precise tempo detection and harmonic key identification for perfect mixing.',
  },
  {
    icon: Guitar,
    title: 'Instrumentation',
    desc: 'See which instruments are present in each section with confidence scores.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Mixing Analysis',
    desc: 'Volume levels, panning, dynamic range, and compression insights.',
  },
  {
    icon: Music2,
    title: 'Chord Progressions',
    desc: 'Detailed chord analysis for each section to understand harmonic movement.',
  },
  {
    icon: Activity,
    title: 'Energy Mapping',
    desc: 'Visualize energy levels across the track to understand arrangement flow.',
  },
];

export default function AnalyzePage() {
  const [audioFileId, setAudioFileId] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const signedIn = Boolean(user);

  const handleUploadComplete = (fileId: string) => {
    setAudioFileId(fileId);
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!audioFileId) return;
    setIsAnalyzing(true);
    setError(null);
    try {
      const result = await analyzeAudio(audioFileId, 8, [
        'chords',
        'tempo',
        'key',
        'instruments',
        'mixing',
        'panning',
      ]);
      navigate(`/results/${result.analysisId}`);
    } catch (err: any) {
      console.error('Analysis error:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Analysis failed to start';
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="analyze-page">
      <div className="container">
        <header className="page-header">
          <p className="hero-eyebrow">
            <span className="hero-eyebrow-live">
              <span className="hero-eyebrow-dot" /> Live
            </span>
            <span className="hero-eyebrow-meta">AI Track Analysis</span>
          </p>
          <h1 className="hero-h1">
            Deep insights for <span className="hero-h1-italic">producers &amp; DJs.</span>
          </h1>
          <p className="page-subtitle">
            Upload a track and KNKT analyzes structure, BPM, key, instrumentation,
            mixing, panning, and chord progressions in 8-bar segments.
          </p>
          <p className="hero-human-aside">
            Want more specific advice from top KNKT artists?{' '}
            <a
              href="https://book.knkt.io/browse?utm_source=audioagent&utm_medium=home_aside"
              target="_blank"
              rel="noopener noreferrer"
              className="hero-human-aside-link"
            >
              Talk to a KNKT expert →
            </a>
          </p>

          {signedIn && (
            <div className="search-section">
              <TrackSearch placeholder="Search your analyzed tracks…" />
            </div>
          )}
        </header>

        {/* Upload first — gated behind auth */}
        <section className="upload-section">
          <div className="upload-section-header">
            <h2 className="upload-title">Upload your track</h2>
            <p className="upload-description">
              Drop your audio file below. We'll analyze it in 8-bar segments and provide
              comprehensive insights about structure, instrumentation, mixing, and more.
            </p>
          </div>

          {/* unchanged structure below */}
          {authLoading ? (
            <div className="upload-auth-checking">
              <LoadingSpinner size="md" color="cyan" />
              <span>Checking your session…</span>
            </div>
          ) : signedIn ? (
            <AudioUploader
              onUploadComplete={handleUploadComplete}
              onError={(err) => setError(err)}
            />
          ) : (
            <div className="upload-locked">
              <div className="upload-locked-icon">
                <Lock size={20} strokeWidth={1.6} />
              </div>
              <h3 className="upload-locked-title">Sign in to upload</h3>
              <p className="upload-locked-description">
                Audio analysis is gated to your KNKT account so your tracks
                stay private and your history follows you.
              </p>
              <Link to="/login" state={{ from: '/' }} className="upload-locked-cta">
                <LogIn size={14} strokeWidth={2} />
                Sign in with KNKT
              </Link>
            </div>
          )}
        </section>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {audioFileId && !isAnalyzing && (
          <div className="analyze-section">
            <div className="ready-badge">
              <CheckCircle2 size={14} strokeWidth={2} />
              <span>Track uploaded and ready</span>
            </div>
            <button
              className="analyze-button"
              onClick={handleAnalyze}
              disabled={isAnalyzing}
            >
              <Sparkles size={14} strokeWidth={2} />
              Start deep analysis
            </button>
            <p className="analyze-hint">
              Analysis typically takes 1–3 minutes depending on track length
            </p>
          </div>
        )}

        {isAnalyzing && (
          <div className="analyzing-section">
            <div className="analyzing-loader-wrap">
              <AILoader text="Analyzing track" size={220} />
            </div>
            <h2 className="analyzing-title">Analyzing your track</h2>
            <p className="analyzing-subtitle">Our AI is extracting deep insights from your audio…</p>
            <MultiStepLoader
              loading
              stepDurationMs={6000}
              steps={[
                'Decoding audio and detecting beat grid',
                'Detecting musical sections (intro, drop, breakdown, outro)',
                'Analyzing BPM, key, and chord progressions',
                'Identifying instruments and mixing characteristics',
                'Predicting genre',
                'Generating comprehensive insights',
              ]}
            />
          </div>
        )}

        {/* Features below */}
        <section className="features-preview">
          <h2 className="features-title">What you'll discover</h2>
          <div className="features-grid">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="feature-card">
                <div className="feature-icon-wrap">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
